import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Divider,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { BarChart } from '@mui/x-charts/BarChart';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { toast } from 'react-toastify';

const getDefaultRange = () => {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
        fromDate: fromDate.toISOString().slice(0, 10),
        toDate: today.toISOString().slice(0, 10),
    };
};

const formatCurrency = (value) => {
    const safeValue = Number(value || 0);
    return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        maximumFractionDigits: 0,
    }).format(safeValue);
};

const downloadCsv = (filename, columns, rows) => {
    const header = columns.map((column) => `"${column.label}"`).join(',');
    const dataLines = rows.map((row) =>
        columns
            .map((column) => {
                const value = row[column.key] ?? '';
                const safeValue = String(value).replace(/"/g, '""');
                return `"${safeValue}"`;
            })
            .join(',')
    );
    const csvContent = [header, ...dataLines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function DailySalesRevenueReport() {
    const [filters, setFilters] = useState({ ...getDefaultRange(), shop: 'All' });
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState({
        range: getDefaultRange(),
        summary: {
            invoiceCount: 0,
            grossSales: 0,
            totalDiscount: 0,
            netSales: 0,
        },
        daily: [],
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://serverfourpaws.ccsoftwares.com/reports/daily-sales', {
                params: {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    shop: filters.shop === 'All' ? null : filters.shop,
                },
            });
            setReportData(response.data);
            if (!response.data.daily.length) {
                toast.info('No records found for the selected range.');
            }
        } catch (error) {
            toast.error('Unable to load daily sales report.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const chartLabels = useMemo(
        () => reportData.daily.map((row) => row.salesDate),
        [reportData.daily]
    );
    const chartRevenue = useMemo(
        () => reportData.daily.map((row) => Number(row.netSales || 0)),
        [reportData.daily]
    );

    const summaryCards = [
        {
            label: 'Net Sales',
            value: formatCurrency(reportData.summary.netSales),
            tone: 'linear-gradient(135deg, #E4F2FF 0%, #F4FAFF 100%)',
        },
        {
            label: 'Invoices',
            value: reportData.summary.invoiceCount || 0,
            tone: 'linear-gradient(135deg, #F6E8FF 0%, #FBF5FF 100%)',
        },
        {
            label: 'Gross Sales',
            value: formatCurrency(reportData.summary.grossSales),
            tone: 'linear-gradient(135deg, #E6F5F4 0%, #F7FFFE 100%)',
        },
        {
            label: 'Discounts',
            value: formatCurrency(reportData.summary.totalDiscount),
            tone: 'linear-gradient(135deg, #F9EAE1 0%, #FFF5EF 100%)',
        },
    ];

    const downloadDailyCsv = () => {
        downloadCsv(
            'daily-sales-revenue',
            [
                { label: 'Date', key: 'salesDate' },
                { label: 'Invoices', key: 'invoiceCount' },
                { label: 'Gross Sales', key: 'grossSales' },
                { label: 'Discounts', key: 'totalDiscount' },
                { label: 'Net Sales', key: 'netSales' },
            ],
            reportData.daily
        );
    };

    return (
        <Container maxWidth={false}>
            <Box mt={0}>
                <Card sx={{ padding: '15px', mb: 3 }}>
                    <CardHeader
                        title="Daily Sales Revenue"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                    />
                    <Divider />
                    <br />
                    <Grid container spacing={2}>
                        <Grid item md={3} xs={12}>
                            <TextField
                                fullWidth
                                label="From Date"
                                name="fromDate"
                                type="date"
                                value={filters.fromDate}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: filters.toDate }}
                            />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <TextField
                                fullWidth
                                label="To Date"
                                name="toDate"
                                type="date"
                                value={filters.toDate}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: filters.fromDate }}
                            />
                        </Grid>
                        <Grid item md={3} xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Branch"
                                name="shop"
                                value={filters.shop}
                                onChange={handleChange}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="All">All Branches</MenuItem>
                                <MenuItem value="Ratnapura">Ratnapura</MenuItem>
                                <MenuItem value="Kalawana">Kalawana</MenuItem>
                                <MenuItem value="Pelmadulla">Pelmadulla</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item md={3} xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                sx={{ width: '100%' }}
                                onClick={fetchReport}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>
                </Card>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {summaryCards.map((card) => (
                        <Grid item xs={12} sm={6} md={4} key={card.label}>
                            <Card sx={{ background: card.tone, boxShadow: '0 10px 20px rgba(0,0,0,0.06)' }}>
                                <CardContent>
                                    <Typography
                                        sx={{
                                            fontSize: '13px',
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                            letterSpacing: '0.8px',
                                        }}
                                    >
                                        {card.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '26px',
                                            fontWeight: 700,
                                            mt: 1,
                                            fontFamily: 'Oswald, sans-serif',
                                        }}
                                    >
                                        {card.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Card sx={{ padding: '15px', mb: 3 }}>
                    <CardHeader
                        title="Daily Net Sales"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={downloadDailyCsv}
                                disabled={!reportData.daily.length}
                            >
                                CSV
                            </Button>
                        }
                    />
                    <Divider />
                    <CardContent>
                        <BarChart
                            height={340}
                            series={[
                                { data: chartRevenue, label: 'Net Sales', color: '#02847A' },
                            ]}
                            xAxis={[{ data: chartLabels, scaleType: 'band' }]}
                            margin={{ top: 20, bottom: 60, left: 40, right: 10 }}
                        />
                    </CardContent>
                </Card>

                <Card sx={{ padding: '15px' }}>
                    <CardHeader
                        title="Daily Breakdown"
                        titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 'bold' }}
                        action={
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadOutlinedIcon />}
                                onClick={downloadDailyCsv}
                                disabled={!reportData.daily.length}
                            >
                                CSV
                            </Button>
                        }
                    />
                    <Divider />
                    <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Invoices</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Gross Sales</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Discounts</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Net Sales</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.daily.length ? (
                                    reportData.daily.map((row) => (
                                        <TableRow key={row.salesDate}>
                                            <TableCell>{row.salesDate}</TableCell>
                                            <TableCell>{row.invoiceCount}</TableCell>
                                            <TableCell>{formatCurrency(row.grossSales)}</TableCell>
                                            <TableCell>{formatCurrency(row.totalDiscount)}</TableCell>
                                            <TableCell>{formatCurrency(row.netSales)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No daily records available.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>
        </Container>
    );
}

export default DailySalesRevenueReport;
